<template>
  <v-container fluid class="list-page grade-list-page">
    <Create
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="getList"
    ></Create>
    <Edit
      :editDialog="editDialog"
      @close="editDialog = false"
      @submit="getList"
      :editGrade="selectedGrade"
    ></Edit>
    <div class="list-page-heading">
      <div>
        <div class="list-page-heading__eyebrow">Academics</div>
        <h1>Grades</h1>
        <p>Review student results by department, academic year, and term.</p>
      </div>
      <div class="list-page-heading__actions">
        <v-btn v-if="checkRightStatus(49)" color="primary" depressed @click="add">
          <v-icon left>mdi-plus</v-icon>
          New grade
        </v-btn>
      </div>
    </div>

    <v-row class="list-metrics grade-list-page__metrics">
      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-file-document-edit-outline</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ formatCount(grades.length) }}</div>
            <div class="list-metric__label">Total grade records</div>
          </div>
        </div>
      </v-col>
      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-domain</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ departmentOptions.length }}</div>
            <div class="list-metric__label">Departments with grades</div>
          </div>
        </div>
      </v-col>
      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-filter-check-outline</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ hasActiveView ? formatCount(filteredGrades.length) : "—" }}</div>
            <div class="list-metric__label">Grades in result</div>
          </div>
        </div>
      </v-col>
      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-account-school-outline</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ hasActiveView ? resultStudentCount : "—" }}</div>
            <div class="list-metric__label">Students in result</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-card :loading="loading" class="list-card grade-list-page__card" elevation="0">
      <div class="list-toolbar grade-list-page__toolbar">
        <v-autocomplete
          v-model="selectedDepartment"
          :items="departmentOptions"
          class="list-toolbar__control grade-filter grade-filter--department"
          item-text="name"
          item-value="id"
          placeholder="Department"
          prepend-inner-icon="mdi-domain"
          background-color="#F8FAF8"
          outlined dense clearable hide-details
          @change="onDepartmentChange"
        />
        <div class="list-toolbar__actions grade-list-page__filter-actions">
          <v-select
            v-model="selectedYear"
            :items="yearOptions"
            class="list-toolbar__control grade-filter"
            item-text="name" item-value="id" placeholder="Academic year"
            background-color="#F8FAF8" outlined dense clearable hide-details
            :disabled="!selectedDepartment"
            @change="onYearChange"
          />
          <v-select
            v-model="selectedTerm"
            :items="termOptions"
            class="list-toolbar__control grade-filter"
            item-text="name" item-value="id" placeholder="Term"
            background-color="#F8FAF8" outlined dense clearable hide-details
            :disabled="!selectedDepartment || !selectedYear"
            @change="onTermChange"
          />
          <v-autocomplete
            v-model="selectedStudent"
            :items="studentOptions"
            class="list-toolbar__control grade-filter grade-filter--student"
            item-text="name" item-value="id" placeholder="Search student"
            prepend-inner-icon="mdi-account-search-outline"
            background-color="#F8FAF8" outlined dense clearable hide-details
            :disabled="!selectedDepartment"
            no-data-text="No students match these filters"
            @change="page = 1"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>Student ID: {{ item.id }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" icon outlined color="primary" :disabled="!hasFilters" aria-label="Clear grade filters" @click="clearFilters">
                <v-icon size="19">mdi-filter-remove-outline</v-icon>
              </v-btn>
            </template>
            <span>Clear filters</span>
          </v-tooltip>
          <v-btn icon outlined color="primary" :loading="loading" aria-label="Refresh grades" @click="getList">
            <v-icon size="19">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
      <v-data-table
        v-if="hasActiveView"
        :headers="headers"
        :items="filteredGrades"
        :loading="loading"
        :sort-by="sortBy"
        sort-desc
        :page.sync="page"
        :items-per-page="itemsPerPage"
        item-key="id"
        loading-text="Loading grade records..."
        class="list-table list-table--responsive grade-list-page__table"
      >
        <template v-slot:item="{ item }">
          <tr class="list-table-row">
            <td data-label="Grade ID"><span class="list-record-id">#{{ item.id }}</span></td>
            <td data-label="Recorded">
              <div class="list-cell-primary">{{ item.createdAt | moment("MMM D, YYYY") }}</div>
              <div class="list-cell-secondary">{{ item.createdAt | moment("from", "now") }}</div>
            </td>
            <td data-label="Student">
              <div class="grade-student-cell">
                <v-avatar size="32" color="green lighten-5" class="mr-2"><v-icon small color="primary">mdi-school-outline</v-icon></v-avatar>
                <span><strong>{{ studentName(item) }}</strong><small>Student ID: {{ item.StudentId }}</small></span>
              </div>
            </td>
            <td data-label="Classroom"><div class="list-cell-primary">{{ item.ClassRoom ? item.ClassRoom.name : "—" }}</div></td>
            <td data-label="Term">
              <div class="list-cell-primary">{{ item.Term ? item.Term.name : "—" }}</div>
              <div v-if="item.Term && item.Term.Year" class="list-cell-secondary">{{ item.Term.Year.name }}</div>
            </td>
            <td data-label="Title"><div class="list-cell-primary">{{ item.title }}</div></td>
            <td data-label="Result"><v-chip small label class="font-weight-bold" :color="gradeColor(item)" outlined>{{ item.ObtainedMarks }} / {{ item.totalMarks }}</v-chip></td>
            <td data-label="Actions" class="text-right action-cell"><v-menu bottom left offset-y>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" icon :aria-label="`Actions for ${studentName(item)}`">
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list class="menu-button">
              <v-list-item v-if="checkRightStatus(51)" @click="editGrade(item)">
                <v-list-item-icon><v-icon small>mdi-pencil-outline</v-icon></v-list-item-icon>
                <v-list-item-title>Edit</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="checkRightStatus(52)" @click="deleteGrade(item.id)">
                <v-list-item-icon><v-icon small color="error">mdi-delete-outline</v-icon></v-list-item-icon>
                <v-list-item-title class="error--text">Delete</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu></td>
          </tr>
        </template>
        <template v-slot:no-data>
          <div class="grade-list-page__empty py-10 text-center">
            <v-icon size="42" color="grey lighten-1">school</v-icon>
            <div class="subtitle-1 mt-3">
              {{ search ? "No matching grades" : "No grades yet" }}
            </div>
            <div class="body-2 grey--text mt-1">
              {{ search ? `No results for “${search}”. Try a student ID, name, class, term, or title.` : "Grade records will appear here." }}
            </div>
            <v-btn v-if="search" text color="primary" class="mt-2" @click="search = ''">
              Clear search
            </v-btn>
          </div>
        </template>
      </v-data-table>
      <div v-else class="grade-list-page__welcome text-center px-4 py-12">
        <v-avatar color="green lighten-5" size="72">
          <v-icon color="green" size="38">mdi-chart-box-outline</v-icon>
        </v-avatar>
        <div class="title mt-4">Choose which grades you want to review</div>
        <div class="body-2 grey--text text--darken-1 mt-2 mx-auto grade-list-page__welcome-copy">
          Select a department first. Then search for a student, or select an academic year and term.
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import gradeService from "../service";
import { mapGetters } from "vuex";

const Create = () => import("./Create");
const Edit = () => import("./Edit");
export default {
  name: "grade-list",
  data() {
    return {
      createDialog: false,
      selectedGrade: null,
      headers: [
        {
          text: "#",
          align: "left",
          value: "id",
          sortable: true,
          width: 72,
        },
        {
          text: "Recorded",
          align: "left",
          value: "createdAt",
          sortable: true,
        },
        {
          text: "Student",
          align: "left",
          value: "Student.fullName",
          sortable: true,
        },
        {
          text: "Classroom",
          align: "left",
          value: "ClassRoom.name",
          sortable: true,
        },
        {
          text: "Term",
          align: "left",
          value: "Term.name",
          sortable: true,
        },
        {
          text: "Title",
          align: "left",
          value: "title",
          sortable: true,
        },
        {
          text: "Result",
          align: "left",
          value: "ObtainedMarks",
          sortable: true,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        },
      ],
      grades: [],
      search: null,
      selectedDepartment: null,
      selectedStudent: null,
      selectedYear: null,
      selectedTerm: null,
      selectedClassRoom: null,
      loading: true,
      page: 1,
      sortBy: "createdAt",
      pageCount: 0,
      itemsPerPage: 10,
      editDialog: null,
    };
  },
  created() {
    this.getList();
    document.title = "taleem - Grade List";
  },
  components: {
    Create,
    Edit,
  },
  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
    hasFilters() {
      return Boolean(
        this.selectedDepartment ||
        this.selectedStudent ||
        this.selectedYear ||
        this.selectedTerm ||
        this.selectedClassRoom ||
        (this.search || "").trim()
      );
    },
    hasActiveView() {
      return Boolean(
        this.selectedDepartment && (
          this.selectedStudent ||
          (this.selectedYear && this.selectedTerm) ||
          (this.search || "").trim()
        )
      );
    },
    departmentOptions() {
      return this.uniqueOptions(
        this.grades
          .filter((grade) => grade.ClassRoom && grade.ClassRoom.Class)
          .map((grade) => ({
            id: this.optionId(grade.ClassRoom.Class.id, grade.ClassRoom.Class.name),
            name: grade.ClassRoom.Class.name,
          }))
      );
    },
    yearOptions() {
      return this.uniqueOptions(
        this.grades
          .filter((grade) => this.gradeDepartmentId(grade) === this.selectedDepartment)
          .filter((grade) => grade.Term && grade.Term.Year)
          .map((grade) => ({
            id: this.optionId(grade.Term.Year.id, grade.Term.Year.name),
            name: grade.Term.Year.name,
          }))
      );
    },
    termOptions() {
      if (!this.selectedYear) return [];
      return this.uniqueOptions(
        this.grades
          .filter((grade) => this.gradeDepartmentId(grade) === this.selectedDepartment)
          .filter((grade) => this.gradeYearId(grade) === this.selectedYear)
          .filter((grade) => grade.Term)
          .map((grade) => ({
            id: this.optionId(grade.Term.id, grade.Term.name),
            name: grade.Term.name,
          }))
      );
    },
    classRoomOptions() {
      return this.uniqueOptions(
        this.grades
          .filter((grade) => this.gradeDepartmentId(grade) === this.selectedDepartment)
          .filter((grade) => !this.selectedYear || this.gradeYearId(grade) === this.selectedYear)
          .filter((grade) => !this.selectedTerm || this.gradeTermId(grade) === this.selectedTerm)
          .filter((grade) => grade.ClassRoom)
          .map((grade) => ({
            id: this.optionId(grade.ClassRoomId, grade.ClassRoom.name),
            name: grade.ClassRoom.name,
          }))
      );
    },
    studentOptions() {
      return this.uniqueOptions(
        this.grades
          .filter((grade) => grade.StudentId != null)
          .filter((grade) => this.gradeDepartmentId(grade) === this.selectedDepartment)
          .filter((grade) => !this.selectedYear || this.gradeYearId(grade) === this.selectedYear)
          .filter((grade) => !this.selectedTerm || this.gradeTermId(grade) === this.selectedTerm)
          .filter((grade) => !this.selectedClassRoom || this.gradeClassRoomId(grade) === this.selectedClassRoom)
          .map((grade) => ({
            id: String(grade.StudentId),
            name: this.studentName(grade),
          }))
      );
    },
    filteredGrades() {
      const query = (this.search || "").trim().toLowerCase();
      if (!this.hasActiveView) return [];

      return this.grades.filter((grade) => {
        if (this.gradeDepartmentId(grade) !== this.selectedDepartment) return false;
        if (this.selectedStudent && String(grade.StudentId) !== this.selectedStudent) return false;
        if (this.selectedYear && this.gradeYearId(grade) !== this.selectedYear) return false;
        if (this.selectedTerm && this.gradeTermId(grade) !== this.selectedTerm) return false;
        if (this.selectedClassRoom && this.gradeClassRoomId(grade) !== this.selectedClassRoom) return false;
        if (!query) return true;

        const term = grade.Term || {};
        const year = term.Year || {};
        const classroom = grade.ClassRoom || {};
        const subject = grade.Subject || {};
        const values = [
          grade.id,
          grade.StudentId,
          grade.title,
          this.studentName(grade),
          classroom.name,
          term.name,
          year.name,
          subject.name,
        ];

        return values.some((value) =>
          String(value == null ? "" : value).toLowerCase().includes(query)
        );
      });
    },
    resultStudentCount() {
      return new Set(this.filteredGrades.map((grade) => grade.StudentId).filter((id) => id != null)).size;
    },
    resultSummary() {
      if (this.loading) return "Loading records…";
      if (!this.hasActiveView) {
        return `${this.grades.length} records available`;
      }
      return `${this.filteredGrades.length} ${this.filteredGrades.length === 1 ? "record" : "records"} shown`;
    },
  },
  methods: {
    add() {
      this.createDialog = !this.createDialog;
    },
    formatCount(value) {
      return new Intl.NumberFormat("en-US").format(Number(value || 0));
    },
    optionId(id, fallback) {
      return String(id == null ? fallback : id);
    },
    uniqueOptions(options) {
      const seen = new Set();
      return options
        .filter((option) => option.id && option.name && !seen.has(option.id) && seen.add(option.id))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    gradeYearId(grade) {
      if (!grade.Term || !grade.Term.Year) return null;
      return this.optionId(grade.Term.Year.id, grade.Term.Year.name);
    },
    gradeDepartmentId(grade) {
      if (!grade.ClassRoom || !grade.ClassRoom.Class) return null;
      return this.optionId(grade.ClassRoom.Class.id, grade.ClassRoom.Class.name);
    },
    gradeTermId(grade) {
      if (!grade.Term) return null;
      return this.optionId(grade.Term.id, grade.Term.name);
    },
    gradeClassRoomId(grade) {
      if (!grade.ClassRoom) return null;
      return this.optionId(grade.ClassRoomId, grade.ClassRoom.name);
    },
    onDepartmentChange() {
      this.selectedYear = null;
      this.selectedTerm = null;
      this.selectedClassRoom = null;
      this.selectedStudent = null;
      this.search = "";
      this.page = 1;
    },
    onYearChange() {
      this.selectedTerm = null;
      this.selectedStudent = null;
      this.page = 1;
    },
    onTermChange() {
      this.selectedStudent = null;
      this.page = 1;
    },
    onContextChange() {
      this.selectedStudent = null;
      this.page = 1;
    },
    clearFilters() {
      this.selectedDepartment = null;
      this.selectedStudent = null;
      this.selectedYear = null;
      this.selectedTerm = null;
      this.selectedClassRoom = null;
      this.search = "";
      this.page = 1;
    },
    studentName(grade) {
      if (!grade || !grade.Student) return "Unknown student";
      const student = grade.Student;
      return student.fullName || [student.firstName, student.lastName].filter(Boolean).join(" ") || "Unknown student";
    },
    gradeColor(grade) {
      const total = Number(grade.totalMarks);
      const obtained = Number(grade.ObtainedMarks);
      if (!total || Number.isNaN(obtained)) return "grey";
      const percentage = (obtained / total) * 100;
      if (percentage >= 80) return "success";
      if (percentage >= 60) return "primary";
      if (percentage >= 40) return "warning";
      return "error";
    },
    getList() {
      this.loading = true;
      this.createDialog = false
      return gradeService.getAll().then((response) => {
        this.grades = response.data;
        return response;
      }).catch((error) => {
        this.$swal("Unable to load grades", error.data && error.data.message ? error.data.message : "Please try again.", "error");
        throw error;
      }).finally(() => {
        this.loading = false;
      });
    },
    editGrade(grade) {
      this.selectedGrade = grade;
      this.editDialog = !this.editDialog;
    },
    deleteGrade(id) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          gradeService.delete(id).then((response) => {
            if (response.data) {
              this.$swal("Deleted!", "Grade has been deleted.", "success");
              this.getList();
            }
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.grade-list-page__toolbar {
  gap: 14px;
}

.grade-list-page__welcome {
  border-top: 1px solid #eeeeee;
}

.grade-list-page__welcome-copy {
  max-width: 560px;
}

.grade-filter {
  min-width: 150px;
  max-width: 210px;
}

.grade-filter--department {
  min-width: 210px;
  max-width: 260px;
}

.grade-filter--student {
  min-width: 230px;
  max-width: 300px;
}

.grade-list-page__filter-actions {
  flex: 1;
  justify-content: flex-end;
}

.grade-student-cell {
  display: flex;
  align-items: center;
}

.grade-student-cell > span {
  display: flex;
  flex-direction: column;
}

.grade-student-cell strong {
  color: #253047;
  font-weight: 700;
}

.grade-student-cell small {
  margin-top: 2px;
  color: #8a968d;
}

.grade-list-page__table ::v-deep tbody tr {
  transition: background 0.15s;
}

.grade-list-page__table ::v-deep tbody tr:hover {
  background: #f7fbf7;
}

.grade-list-page__table ::v-deep .v-chip {
  min-width: 74px;
  justify-content: center;
  border-radius: 8px;
  font-weight: 700;
}

@media (max-width: 600px) {
  .grade-list-page__toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .grade-list-page__filter-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .grade-filter,
  .grade-filter--department,
  .grade-filter--student {
    min-width: 0;
    max-width: none;
    width: 100%;
  }
}
</style>
